import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { ApiResponse, ApiKey, ApiKeyStatsResponse, ApiKeyLog, CreateApiKeyInput, UpdateApiKeyInput } from '@/types';

export function useApiKeys() {
  return useQuery({
    queryKey: ['apiKeys'],
    queryFn: async (): Promise<ApiKey[]> => {
      const response = await api.get<ApiResponse<ApiKey[]>>('/api-keys');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch API keys');
      }
      return response.data.data;
    },
  });
}

export function useApiKeysEnhanced() {
  return useQuery({
    queryKey: ['apiKeysEnhanced'],
    queryFn: async (): Promise<ApiKey[]> => {
      const response = await api.get<ApiResponse<ApiKey[]>>('/api-keys/enhanced');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch API keys');
      }
      return response.data.data;
    },
  });
}

export function useApiKeyStats(keyId: number | null, days: number = 7) {
  return useQuery({
    queryKey: ['apiKeyStats', keyId, days],
    queryFn: async (): Promise<ApiKeyStatsResponse> => {
      const response = await api.get<ApiResponse<ApiKeyStatsResponse>>(
        `/api-keys/${keyId}/stats?days=${days}`
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch API key stats');
      }
      return response.data.data;
    },
    enabled: !!keyId,
  });
}

export function useApiKeyLogs(keyId: number | null, limit: number = 50) {
  return useQuery({
    queryKey: ['apiKeyLogs', keyId, limit],
    queryFn: async (): Promise<ApiKeyLog[]> => {
      const response = await api.get<ApiResponse<ApiKeyLog[]>>(
        `/api-keys/${keyId}/logs?limit=${limit}`
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch API key logs');
      }
      return response.data.data;
    },
    enabled: !!keyId,
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateApiKeyInput): Promise<ApiKey & { key: string; id: number }> => {
      const response = await api.post<ApiResponse<ApiKey & { key: string; id: number }>>('/api-keys', data);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to create API key');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKeysEnhanced'] });
    },
  });
}

export function useUpdateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateApiKeyInput }) => {
      const response = await api.patch<ApiResponse>(`/api-keys/${id}`, data);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to update API key');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKeysEnhanced'] });
    },
  });
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (keyId: number) => {
      const response = await api.delete<ApiResponse>(`/api-keys/${keyId}`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to revoke API key');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKeysEnhanced'] });
    },
  });
}
